import React from "react";

const useLatestRef = (value) => {
    const ref = React.useRef(value);
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref;
};

export function useAmplifyIncrementalList({
    fetchPage,       // ({limit,nextToken,filter}) => Promise<{items,nextToken}>
    searchText,
    buildFilter,     // (q) => GraphQL filter
    pageSize = 50,
    enabled = true,
    getItemKey = (item) => item.id,
} = {}) {
    const fetchPageRef = useLatestRef(fetchPage);
    const buildFilterRef = useLatestRef(buildFilter);
    const getItemKeyRef = useLatestRef(getItemKey);

    const [items, setItems] = React.useState([]);
    const [nextToken, setNextToken] = React.useState(null);
    const [hasMore, setHasMore] = React.useState(true);

    const [isLoadingInitial, setIsLoadingInitial] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);

    const bottomRef = React.useRef(null);

    const runIdRef = React.useRef(0);
    const loadingMoreRef = React.useRef(false);

    const normalizedSearch = (searchText || "").trim();
    const isFilterActive = normalizedSearch.length > 0;

    const resetState = React.useCallback(() => {
        setItems([]);
        setNextToken(null);
        setHasMore(true);
    }, []);

    const appendDedup = React.useCallback((newItems) => {
        if (!newItems || newItems.length === 0) return;

        setItems((prev) => {
            const keyFn = getItemKeyRef.current || ((x) => x.id);
            const seen = new Set(prev.map(keyFn));
            const merged = prev.slice();

            for (const it of newItems) {
                const k = keyFn(it);
                if (!seen.has(k)) {
                    seen.add(k);
                    merged.push(it);
                }
            }
            return merged;
        });
    }, [getItemKeyRef]);

    const loadFirstPageNoFilter = React.useCallback(async () => {
        const runId = ++runIdRef.current;

        setIsLoadingInitial(true);
        setIsSearching(false);
        setIsLoadingMore(false);
        loadingMoreRef.current = false;

        resetState();

        try {
            const res = await fetchPageRef.current?.({
                limit: pageSize,
                nextToken: null,
                filter: null,
            });

            if (runId !== runIdRef.current) return;

            const newItems = res?.items || [];
            setItems(newItems);

            const nt = res?.nextToken || null;
            setNextToken(nt);
            setHasMore(!!nt);
        } finally {
            if (runId === runIdRef.current) setIsLoadingInitial(false);
        }
    }, [fetchPageRef, pageSize, resetState]);

    const loadMoreNoFilter = React.useCallback(async () => {
        if (isFilterActive) return;
        if (!hasMore || !nextToken) return;
        if (loadingMoreRef.current) return;

        loadingMoreRef.current = true;
        setIsLoadingMore(true);

        try {
            const res = await fetchPageRef.current?.({
                limit: pageSize,
                nextToken,
                filter: null,
            });

            appendDedup(res?.items || []);

            const nt = res?.nextToken || null;
            setNextToken(nt);
            setHasMore(!!nt);
        } finally {
            setIsLoadingMore(false);
            loadingMoreRef.current = false;
        }
    }, [appendDedup, fetchPageRef, hasMore, isFilterActive, nextToken, pageSize]);

    const startIncrementalSearch = React.useCallback(async () => {
        const q = normalizedSearch;
        const runId = ++runIdRef.current;

        setIsLoadingInitial(true);
        setIsLoadingMore(false);
        setIsSearching(true);
        loadingMoreRef.current = false;

        resetState();
        setHasMore(false);

        const filterFn = buildFilterRef.current;
        const filter = filterFn ? filterFn(q) : null;

        try {
            let localNextToken = null;

            do {
                if (runId !== runIdRef.current) return;

                const res = await fetchPageRef.current?.({
                    limit: pageSize,
                    nextToken: localNextToken,
                    filter,
                });

                if (runId !== runIdRef.current) return;

                appendDedup(res?.items || []);

                localNextToken = res?.nextToken || null;
                setNextToken(localNextToken);

                // permite render incremental
                await new Promise((r) => setTimeout(r, 0));
            } while (localNextToken);
        } finally {
            if (runId === runIdRef.current) {
                setIsLoadingInitial(false);
                setIsSearching(false);
            }
        }
    }, [appendDedup, buildFilterRef, fetchPageRef, normalizedSearch, pageSize, resetState]);

    // 🔒 Efecto principal: depende SOLO del texto/estado, no de funciones
    React.useEffect(() => {
        if (!enabled) return;

        // cancela cualquier run anterior
        runIdRef.current++;

        if (isFilterActive) startIncrementalSearch();
        else loadFirstPageNoFilter();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, isFilterActive, normalizedSearch]);

    // Infinite scroll SOLO sin filtro
    React.useEffect(() => {
        if (!enabled) return;
        if (isFilterActive) return;

        const el = bottomRef.current;
        if (!el) return;

        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMoreNoFilter();
            },
            { root: null, threshold: 0.1 }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, [enabled, isFilterActive, loadMoreNoFilter]);

    return {
        items,
        nextToken,
        hasMore,

        isFilterActive,
        isLoadingInitial,
        isLoadingMore,
        isSearching,

        bottomRef,

        reload: () => {
            if (isFilterActive) startIncrementalSearch();
            else loadFirstPageNoFilter();
        },
        cancelCurrentRun: () => {
            runIdRef.current++;
        },
    };
}